/**
 * applicationId の取得・永続化ユーティリティ。
 *
 * 優先順位:
 * 1. メモリ上の `cachedApplicationId`（起動時に URL または Native から取得済み）
 * 2. ローカルストレージの保存値
 * 3. 既定の WEB アプリ ID（`~/lib/constants` の `APPLICATION_ID`）
 */
import {
  APPLICATION_ID as DEFAULT_APPLICATION_ID,
  HIBANA_APPLICATION_ID,
  RENKA_ANDROID_APPLICATION_ID,
  SAKURA_APPLICATION_ID,
} from "~/constants/app";
import { native } from "~/lib/nativeBridge";
import { isWebView } from "~/lib/webview";

// Utage-Web と同じキー名で揃える
export const APPLICATION_ID_STORAGE_KEY = "applicationId";

const NATIVE_APP_PARAM_KEY = "app";
const NATIVE_APP_ID_PARAM_KEY = "appId";

let cachedApplicationId: string | null = null;

export const isNativeApp = (search: URLSearchParams): boolean => {
  return search.get(NATIVE_APP_PARAM_KEY) === "native";
};

/**
 * applicationId を初期化する。main.tsx の起動シーケンスで呼ぶ。
 *
 * - URL に `?appId=` があればそれを使う（最優先）
 * - なければ WebView 環境なら Native の Keychain から取得する（最大5回リトライ）
 * - Web 環境なら何もしない（getCurrentApplicationId が定数を返す）
 */
export const initApplicationId = async (
  search: URLSearchParams,
): Promise<void> => {
  const fromUrl = search.get(NATIVE_APP_ID_PARAM_KEY);
  if (fromUrl) {
    cachedApplicationId = fromUrl;
    safeSetStoredApplicationId(fromUrl);
    return;
  }

  if (!isWebView()) return;

  const RETRY_INTERVAL_MS = 2000;
  const MAX_RETRIES = 5;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const authInfo = await native.requestAuthInfo();
    if (authInfo?.applicationId) {
      cachedApplicationId = authInfo.applicationId;
      safeSetStoredApplicationId(authInfo.applicationId);
      return;
    }
    if (attempt < MAX_RETRIES - 1) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
    }
  }
};

/**
 * 現在のリクエストで使用すべき application_id を返す。
 * 優先順位: メモリキャッシュ > localStorage > 既定値。
 */
export const getCurrentApplicationId = (): string => {
  return (
    cachedApplicationId ??
    safeGetStoredApplicationId() ??
    DEFAULT_APPLICATION_ID
  );
};

/** 現在のアプリが Sakura（applicationId=84）かどうか。各 feature の出し分け判定で共用する。 */
export const isSakuraApp = (): boolean =>
  getCurrentApplicationId() === SAKURA_APPLICATION_ID;

/**
 * 現在のアプリが Carina（applicationId=95、既定の WEB アプリ ID でもある）かどうか。
 * Hokage(93) / Renka(76) / Sakura(84) を除外したい出し分け判定で使う。
 */
export const isCarinaApp = (): boolean =>
  getCurrentApplicationId() === DEFAULT_APPLICATION_ID;

/**
 * 「あなたが以前話した女性」セクションの対象ブランド（Carina=95 / Renka Android=76）。
 * Sakura(84) は専用の SakuraDiscoverTemplate を使うため対象外。
 */
const TALKED_BEFORE_SECTION_APPLICATION_IDS: readonly string[] = [
  DEFAULT_APPLICATION_ID,
  RENKA_ANDROID_APPLICATION_ID,
];

/** 現在のアプリが「あなたが以前話した女性」セクションの対象ブランドかどうか。 */
export const isTalkedBeforeSectionApp = (): boolean =>
  TALKED_BEFORE_SECTION_APPLICATION_IDS.includes(getCurrentApplicationId());

/** ¥マークを小さく表示する対象ブランド（Carina=95 / Sakura=84 / Renka Android=76） */
const SMALL_YEN_MARK_APPLICATION_IDS: readonly string[] = [
  DEFAULT_APPLICATION_ID,
  SAKURA_APPLICATION_ID,
  RENKA_ANDROID_APPLICATION_ID,
];

/**
 * 決済画面の¥マークを小さく表示すべきブランドかどうか。
 * Sina(83) / Renka iOS(72) は対象外で現状の表記サイズを維持する。
 */
export const isSmallYenMarkApp = (): boolean =>
  SMALL_YEN_MARK_APPLICATION_IDS.includes(getCurrentApplicationId());

/**
 * applicationId → 表示用アプリ名のマップ。
 * `lib/theme/palettes/` の appName はテーマ色専用の登録状況（未登録 appId は Sina にフォールバック）と
 * ずれるため、文言表示用にはこちらの専用マップを使う（テーマ色とは別軸で管理する）。
 */
const APP_DISPLAY_NAME_BY_APPLICATION_ID: Record<string, string> = {
  "84": "Sakura",
  "76": "Renka", // Renka Android
  "95": "Carina",
};

/**
 * 現在の applicationId に対応する表示用アプリ名を返す。
 * 未登録の appId は既定の applicationId（Carina）の名前にフォールバックする。
 */
export const getCurrentAppDisplayName = (): string => {
  return (
    APP_DISPLAY_NAME_BY_APPLICATION_ID[getCurrentApplicationId()] ??
    APP_DISPLAY_NAME_BY_APPLICATION_ID[DEFAULT_APPLICATION_ID]
  );
};

/**
 * 問い合わせフォームの既定サポート宛先。
 * applicationId 別マップ（`SUPPORT_EMAIL_BY_APPLICATION_ID`）に無い appId のフォールバック先。
 */
const DEFAULT_SUPPORT_EMAIL = "support@utage-web-support.awsapps.com";

/**
 * applicationId → サポート問い合わせメール宛先のマップ。
 * 未登録の appId は `DEFAULT_SUPPORT_EMAIL` にフォールバックする。
 * ※ Sakura のみ `.net` ドメイン（他ブランドは `-app.com`）なので注意。
 */
const SUPPORT_EMAIL_BY_APPLICATION_ID: Record<string, string> = {
  [DEFAULT_APPLICATION_ID]: "support@carina-app.com", // Carina (95)
  [SAKURA_APPLICATION_ID]: "support@sakura-app.net", // Sakura (84)
  [RENKA_ANDROID_APPLICATION_ID]: "support@renka-app.com", // Renka Android (76)
  [HIBANA_APPLICATION_ID]: "support@hibana-app.com", // HIBANA (132)
};

/**
 * 現在の applicationId に対応するサポート問い合わせ先メールアドレスを返す。
 * 未登録の appId（Sina=83 / Renka iOS=72 / Hokage=93 等）は既定の utage 宛にフォールバックする。
 */
export const getCurrentSupportEmail = (): string =>
  SUPPORT_EMAIL_BY_APPLICATION_ID[getCurrentApplicationId()] ??
  DEFAULT_SUPPORT_EMAIL;

/**
 * URL クエリパラメータから appId を読み取ってメモリ + localStorage に保存する。
 * 存在しなければ何もしない（既存値を温存）。
 */
export const persistNativeApplicationId = (search: URLSearchParams): void => {
  const appId = search.get(NATIVE_APP_ID_PARAM_KEY);
  if (!appId) return;
  cachedApplicationId = appId;
  safeSetStoredApplicationId(appId);
};

/**
 * localStorage に applicationId が未保存なら既定値を書き込む（冪等）。
 * 既存値は触らない（ログイン済みユーザーの ID 上書き防止）。
 */
export const ensureApplicationIdPersisted = (): void => {
  if (typeof window === "undefined") return;
  try {
    const stored = localStorage.getItem(APPLICATION_ID_STORAGE_KEY);
    if (stored && stored.length > 0) return;
    localStorage.setItem(APPLICATION_ID_STORAGE_KEY, DEFAULT_APPLICATION_ID);
  } catch {
    // noop
  }
};

/**
 * テスト用: メモリキャッシュをリセットする。
 */
export const resetApplicationIdState = (): void => {
  cachedApplicationId = null;
};

const safeGetStoredApplicationId = (): string | null => {
  try {
    const stored = localStorage.getItem(APPLICATION_ID_STORAGE_KEY);
    return stored && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
};

const safeSetStoredApplicationId = (id: string): void => {
  try {
    localStorage.setItem(APPLICATION_ID_STORAGE_KEY, id);
  } catch {
    // noop
  }
};
